import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  Button,
  Card,
  H2,
  H4,
  Input,
  Paragraph,
  Spinner,
  useMedia,
  XStack,
  YStack,
} from 'tamagui';
import { Navigate } from 'react-router-dom';
import {
  api,
  type AdminUserRow,
  type ContactMessageDto,
  type VrdbRefreshRequestResult,
  type VrdbStatus,
} from '../lib/api';
import { useMe } from '../lib/user-prefs';

export function Admin() {
  const { me, isLoading: meLoading } = useMe();
  const [q, setQ] = useState('');
  const qc = useQueryClient();

  const statsQuery = useQuery({
    queryKey: ['admin-stats'],
    queryFn: api.adminStats,
    enabled: me?.role === 'ADMIN',
  });
  const usersQuery = useQuery({
    queryKey: ['admin-users', q],
    queryFn: () => api.adminListUsers({ limit: 50, q: q || undefined }),
    enabled: me?.role === 'ADMIN',
  });
  const contactQuery = useQuery({
    queryKey: ['admin-contact'],
    queryFn: api.adminListContact,
    enabled: me?.role === 'ADMIN',
  });
  const vrdbQuery = useQuery({ queryKey: ['admin', 'vrdb'], queryFn: api.adminVrdbStatus });
  const vrdbRefresh = useMutation({
    mutationFn: api.adminRequestVrdbRefresh,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'vrdb'] }),
  });

  if (meLoading) return <Spinner />;
  if (me?.role !== 'ADMIN') return <Navigate to="/" replace />;

  return (
    <YStack gap="$4">
      <H2>Admin</H2>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <H4>System stats</H4>
          {statsQuery.isLoading ? (
            <Spinner />
          ) : statsQuery.isError ? (
            <Paragraph color="$red10">Failed to load stats.</Paragraph>
          ) : statsQuery.data ? (
            <XStack flexWrap="wrap" gap="$3">
              <StatTile label="Users" value={statsQuery.data.users} />
              <StatTile label="Admins" value={statsQuery.data.admins} />
              <StatTile label="Vehicles" value={statsQuery.data.vehicles} />
              <StatTile label="Service logs" value={statsQuery.data.serviceLogs} />
              <StatTile
                label="Scheduled services"
                value={statsQuery.data.scheduledServices}
              />
              <StatTile
                label="Notifications"
                value={`${statsQuery.data.notifications.unread} unread / ${statsQuery.data.notifications.total}`}
              />
              <StatTile
                label="Cached schedules"
                value={
                  statsQuery.data.cachedSchedules < 0
                    ? 'n/a'
                    : statsQuery.data.cachedSchedules
                }
              />
            </XStack>
          ) : null}
        </YStack>
      </Card>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <H4>Canadian recall data</H4>
          <VrdbStatusCard query={vrdbQuery} refresh={vrdbRefresh} />
        </YStack>
      </Card>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <XStack gap="$2" alignItems="center" justifyContent="space-between">
            <H4>Users</H4>
            <Input
              value={q}
              onChangeText={setQ}
              placeholder="Search email…"
              width={240}
              maxWidth="50vw"
            />
          </XStack>
          {usersQuery.isLoading ? (
            <Spinner />
          ) : usersQuery.isError ? (
            <Paragraph color="$danger">Failed to load users.</Paragraph>
          ) : !usersQuery.data || usersQuery.data.rows.length === 0 ? (
            <Paragraph color="$color10">No users match.</Paragraph>
          ) : (
            <YStack gap="$2">
              <UsersList rows={usersQuery.data.rows} />
              <Paragraph size="$1" color="$color10" marginTop="$2">
                Showing {usersQuery.data.rows.length} of {usersQuery.data.total}
              </Paragraph>
            </YStack>
          )}
        </YStack>
      </Card>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <H4>Help requests</H4>
          {contactQuery.isLoading ? (
            <Spinner />
          ) : contactQuery.isError ? (
            <Paragraph color="$danger">Failed to load help requests.</Paragraph>
          ) : !contactQuery.data || contactQuery.data.length === 0 ? (
            <Paragraph color="$color10">No help requests yet.</Paragraph>
          ) : (
            <ContactList rows={contactQuery.data} />
          )}
        </YStack>
      </Card>
    </YStack>
  );
}

/**
 * The Canadian recall data card: freshness, counts, and a manual refresh button.
 * Extracted (like `ContactList`/`UsersList` below) so the several independent,
 * genuinely-optional pieces of status here don't inflate `Admin`'s own complexity.
 */
function VrdbStatusCard({
  query,
  refresh,
}: {
  query: UseQueryResult<VrdbStatus>;
  refresh: UseMutationResult<VrdbRefreshRequestResult, Error, void>;
}) {
  if (query.isLoading) return <Spinner />;
  if (query.isError) return <Paragraph color="$red10">Failed to load refresh status.</Paragraph>;
  if (!query.data) return null;
  const status = query.data;

  return (
    <YStack gap="$2">
      <Paragraph>
        {status.lastSuccessAt
          ? `Last refreshed ${new Date(status.lastSuccessAt).toLocaleDateString()} — ${status.daysSinceSuccess} days ago`
          : 'Never refreshed.'}
      </Paragraph>
      {status.recallCount !== null ? (
        <Paragraph size="$2" color="$color10">
          {status.recallCount.toLocaleString()} recalls ·{' '}
          {status.modelCount?.toLocaleString() ?? '—'} model rows
        </Paragraph>
      ) : null}
      {status.isStale ? (
        <Paragraph color="$red10">
          Data is stale. The refresh runs on a laptop that must be online — check that the
          scheduled task is still enabled.
        </Paragraph>
      ) : null}
      {status.lastAttemptStatus === 'error' ? (
        <Paragraph size="$2" color="$red10">
          Last attempt failed: {status.lastAttemptError ?? 'unknown error'}
        </Paragraph>
      ) : null}
      {status.pending || refresh.isSuccess ? (
        <Paragraph size="$2" color="$color10">
          Requested — runs within 15 minutes once the laptop is online.
        </Paragraph>
      ) : (
        <XStack>
          <Button onPress={() => refresh.mutate()} disabled={refresh.isPending}>
            Request refresh
          </Button>
        </XStack>
      )}
    </YStack>
  );
}

/**
 * Help requests render as a table on wide screens and stacked cards on mobile,
 * mirroring the users list so the admin page stays responsive at 375px.
 */
function ContactList({ rows }: { rows: ContactMessageDto[] }) {
  const media = useMedia();
  if (media.gtSm) return <ContactTable rows={rows} />;
  return (
    <YStack gap="$2">
      {rows.map((m) => (
        <ContactCard key={m.id} message={m} />
      ))}
    </YStack>
  );
}

function ContactTable({ rows }: { rows: ContactMessageDto[] }) {
  return (
    <YStack overflow="scroll">
      <YStack gap="$1" minWidth={TABLE_MIN_WIDTH}>
        <XStack gap="$2" paddingVertical="$1">
          <Paragraph flex={2} fontWeight="600" size="$2">
            From
          </Paragraph>
          <Paragraph flex={2} fontWeight="600" size="$2">
            Subject
          </Paragraph>
          <Paragraph flex={3} fontWeight="600" size="$2">
            Message
          </Paragraph>
          <Paragraph flex={1} fontWeight="600" size="$2">
            Received
          </Paragraph>
        </XStack>
        {rows.map((m) => (
          <XStack
            key={m.id}
            gap="$2"
            paddingVertical="$1"
            borderTopWidth={1}
            borderColor="$color4"
          >
            <Paragraph flex={2} size="$2">
              {m.fromEmail}
            </Paragraph>
            <Paragraph flex={2} size="$2" color="$color11">
              {m.subject ?? '—'}
            </Paragraph>
            <Paragraph flex={3} size="$2" color="$color11">
              {m.body}
            </Paragraph>
            <Paragraph flex={1} size="$2" color="$color10">
              {new Date(m.createdAt).toLocaleDateString()}
            </Paragraph>
          </XStack>
        ))}
      </YStack>
    </YStack>
  );
}

function ContactCard({ message }: { message: ContactMessageDto }) {
  return (
    <YStack
      gap="$1"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$color2"
    >
      <Paragraph fontWeight="600" size="$3">
        {message.subject ?? 'No subject'}
      </Paragraph>
      <Paragraph size="$2" color="$color10">
        {message.fromEmail} · {new Date(message.createdAt).toLocaleDateString()}
      </Paragraph>
      <Paragraph size="$2" color="$color11">
        {message.body}
      </Paragraph>
    </YStack>
  );
}

/**
 * Users render as a scrollable 6-column table on wide screens and as stacked
 * cards on mobile, so the data never forces horizontal page overflow at 375px.
 */
function UsersList({ rows }: { rows: AdminUserRow[] }) {
  const media = useMedia();
  if (media.gtSm) return <UsersTable rows={rows} />;
  return (
    <YStack gap="$2">
      {rows.map((u) => (
        <UserCard key={u.id} user={u} />
      ))}
    </YStack>
  );
}

const TABLE_MIN_WIDTH = 720;

function UsersTable({ rows }: { rows: AdminUserRow[] }) {
  return (
    // Horizontal-scroll wrapper: the table keeps its column layout but scrolls
    // within the card instead of blowing out the page width.
    <YStack overflow="scroll">
      <YStack gap="$1" minWidth={TABLE_MIN_WIDTH}>
        <XStack gap="$2" paddingVertical="$1">
          <Paragraph flex={2} fontWeight="600" size="$2">
            Email
          </Paragraph>
          <Paragraph flex={1} fontWeight="600" size="$2">
            Role
          </Paragraph>
          <Paragraph flex={1} fontWeight="600" size="$2">
            Plan
          </Paragraph>
          <Paragraph flex={1} fontWeight="600" size="$2">
            Country
          </Paragraph>
          <Paragraph flex={1} fontWeight="600" size="$2" textAlign="right">
            Vehicles
          </Paragraph>
          <Paragraph flex={2} fontWeight="600" size="$2">
            Joined
          </Paragraph>
        </XStack>
        {rows.map((u) => (
          <XStack
            key={u.id}
            gap="$2"
            paddingVertical="$1"
            borderTopWidth={1}
            borderColor="$color4"
          >
            <Paragraph flex={2} size="$2">
              {u.email}
            </Paragraph>
            <Paragraph flex={1} size="$2" color={u.role === 'ADMIN' ? '$accent' : '$color11'}>
              {u.role}
            </Paragraph>
            <Paragraph flex={1} size="$2">
              {u.plan}
            </Paragraph>
            <Paragraph flex={1} size="$2">
              {u.country}
            </Paragraph>
            <Paragraph flex={1} size="$2" textAlign="right">
              {u.vehicleCount}
            </Paragraph>
            <Paragraph flex={2} size="$2" color="$color10">
              {new Date(u.createdAt).toLocaleDateString()}
            </Paragraph>
          </XStack>
        ))}
      </YStack>
    </YStack>
  );
}

function UserCard({ user }: { user: AdminUserRow }) {
  return (
    <YStack
      gap="$1"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$color2"
    >
      <Paragraph fontWeight="600" size="$3">
        {user.email}
      </Paragraph>
      <XStack gap="$2" flexWrap="wrap">
        <Paragraph size="$2" color={user.role === 'ADMIN' ? '$accent' : '$color10'}>
          {user.role}
        </Paragraph>
        <Paragraph size="$2" color="$color10">
          · {user.plan}
        </Paragraph>
        <Paragraph size="$2" color="$color10">
          · {user.country}
        </Paragraph>
        <Paragraph size="$2" color="$color10">
          · {user.vehicleCount} vehicle{user.vehicleCount === 1 ? '' : 's'}
        </Paragraph>
      </XStack>
      <Paragraph size="$1" color="$color10">
        Joined {new Date(user.createdAt).toLocaleDateString()}
      </Paragraph>
    </YStack>
  );
}

function StatTile({ label, value }: { label: string; value: number | string }) {
  return (
    <YStack
      minWidth={150}
      padding="$3"
      borderRadius="$3"
      backgroundColor="$color2"
      gap="$1"
    >
      <Paragraph size="$1" color="$color10">
        {label}
      </Paragraph>
      <Paragraph size="$6" fontWeight="700">
        {value}
      </Paragraph>
    </YStack>
  );
}
